// CustomSubtradeReviewPage — Phase 4 / 4+ admin template editor.
//
// Lists every brio_trade subtrade (canonical + custom) grouped by trade
// chapter (1..11). Admins / supervisors can:
//   - Inline-edit label / default unit / default line type / sort / note
//     (boq.update_subtrade RPC, which also relabels every live project_items
//     row that referenced the old label).
//   - Delete a canonical or custom subtrade entirely (boq.delete_subtrade,
//     refuses if any project_items still reference it).
//   - On custom rows only: Promote into canonical, Merge into a canonical
//     sibling, or Reject outright (Phase 4 RPCs).
//
// Role gating happens server-side inside the RPCs, so the page renders
// unconditionally and non-admins simply see a friendly error if they try to
// act.

import React, { useMemo, useState } from 'react';
import { GripVertical } from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  BrioCard,
  BrioButton,
  BrioInput,
  BrioSelect,
  BrioModal,
  BrioModalBody,
  BrioModalField,
} from '../../components/BrioComponents';
import useBoqCustomSubtradeReview from '../hooks/useBoqCustomSubtradeReview';
import { useBoqTaxonomies } from '../hooks/useBoqTaxonomies';

const UNIT_OPTIONS = ['lump', 'sqm', 'cum', 'rmt', 'nos', 'kg', 'MT'];
const LINE_TYPE_OPTIONS = ['allowance', 'rate_only', 'lump_sum', 'provisional_sum', 'prime_cost'];

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return iso; }
}

function Banner({ tone, children }) {
  if (!children) return null;
  const bg = tone === 'error' ? '#fbebea' : '#eaf6ec';
  const fg = tone === 'error' ? '#a32d28' : '#1f6b34';
  return (
    <div style={{ background: bg, color: fg, padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '1rem', fontSize: '0.9rem' }}>
      {children}
    </div>
  );
}

function AddSubtradeModal({ tradeLabel, onCancel, onConfirm, submitting }) {
  const [label, setLabel] = useState('');
  const [unit, setUnit] = useState('lump');
  const [lineType, setLineType] = useState('allowance');
  const [sortOrder, setSortOrder] = useState('');
  const [note, setNote] = useState('');

  const canSave = label.trim().length >= 2 && !submitting;

  return (
    <BrioModal title={`Add subtrade to ${tradeLabel}`} onClose={onCancel} width="md">
      <BrioModalBody>
        <BrioModalField label="Subtrade name" hint="Short, human-readable. Use sentence case.">
          <BrioInput value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Pile foundation" autoFocus />
        </BrioModalField>
        <BrioModalField label="Default unit">
          <BrioSelect value={unit} onChange={(e) => setUnit(e.target.value)}>
            {UNIT_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
          </BrioSelect>
        </BrioModalField>
        <BrioModalField label="Default line type">
          <BrioSelect value={lineType} onChange={(e) => setLineType(e.target.value)}>
            {LINE_TYPE_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
          </BrioSelect>
        </BrioModalField>
        <BrioModalField label="Sort order" hint="Leave blank to append after the existing subtrades.">
          <BrioInput type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} placeholder="auto" />
        </BrioModalField>
        <BrioModalField label="Note (optional)" hint="Internal note. Helps future admins / QS.">
          <BrioInput value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Used in coastal projects" />
        </BrioModalField>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
          <BrioButton variant="secondary" onClick={onCancel}>Cancel</BrioButton>
          <BrioButton
            variant="primary"
            disabled={!canSave}
            onClick={() => onConfirm({
              label: label.trim(),
              defaultUnit: unit,
              defaultLineType: lineType,
              sortOrder: sortOrder === '' ? null : parseInt(sortOrder, 10),
              note: note.trim() || null,
              isCustom: false,
            })}
          >
            {submitting ? 'Adding…' : 'Add subtrade'}
          </BrioButton>
        </div>
      </BrioModalBody>
    </BrioModal>
  );
}

function MergePicker({ row, canonicalTargets, onCancel, onConfirm, submitting }) {
  const [targetId, setTargetId] = useState(canonicalTargets[0]?.nodeId || '');
  return (
    <BrioModal
      title={`Merge "${row.subtradeLabel}" into a canonical subtrade`}
      onClose={onCancel}
      width="md"
    >
      <BrioModalBody>
        <p style={{ fontSize: '0.9rem', color: 'var(--brio-text-muted)', marginBottom: '1rem' }}>
          Every project line currently tagged <strong>{row.tradeLabel} / {row.subtradeLabel}</strong>
          {' '}({row.usageCount} line{row.usageCount === 1 ? '' : 's'} across {row.distinctProjects} project{row.distinctProjects === 1 ? '' : 's'})
          {' '}will be relabelled to the canonical target. The custom subtrade is then deleted.
        </p>
        <BrioModalField label="Canonical target (same trade)">
          <BrioSelect value={targetId} onChange={(e) => setTargetId(e.target.value)}>
            {canonicalTargets.length === 0 && (
              <option value="">No canonical subtrades available under this trade</option>
            )}
            {canonicalTargets.map((t) => (
              <option key={t.nodeId} value={t.nodeId}>{t.subtradeLabel}</option>
            ))}
          </BrioSelect>
        </BrioModalField>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
          <BrioButton variant="secondary" onClick={onCancel}>Cancel</BrioButton>
          <BrioButton variant="primary" disabled={!targetId || submitting} onClick={() => onConfirm(targetId)}>
            {submitting ? 'Merging…' : 'Merge'}
          </BrioButton>
        </div>
      </BrioModalBody>
    </BrioModal>
  );
}

function EditableRow({ row, onSave, onDelete, onPromote, onMerge, onReject, submitting, mergeDisabled }) {
  const [editing, setEditing] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.nodeId, disabled: editing });
  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: isDragging ? '#fffae5' : undefined,
    opacity: isDragging ? 0.85 : 1,
  };
  const [draft, setDraft] = useState({
    label: row.subtradeLabel,
    defaultUnit: row.defaultUnit || 'lump',
    defaultLineType: row.defaultLineType || 'allowance',
    sortOrder: row.templateSortOrder || 0,
    note: row.note || '',
  });

  // Re-seed draft when row swaps under us (e.g. after a refresh).
  React.useEffect(() => {
    if (!editing) {
      setDraft({
        label: row.subtradeLabel,
        defaultUnit: row.defaultUnit || 'lump',
        defaultLineType: row.defaultLineType || 'allowance',
        sortOrder: row.templateSortOrder || 0,
        note: row.note || '',
      });
    }
  }, [row, editing]);

  const cellStyle = { padding: '0.5rem 0.6rem', verticalAlign: 'middle' };
  const muted = { color: 'var(--brio-text-muted)' };

  if (!editing) {
    return (
      <tr ref={setNodeRef} style={{ borderTop: '1px solid var(--brio-border-subtle, #eee)', ...dragStyle }}>
        <td style={{ ...cellStyle, width: 28, padding: '0.5rem 0.25rem', cursor: 'grab', color: 'var(--brio-text-muted)' }}
            {...attributes} {...listeners}
            title="Drag to reorder">
          <GripVertical size={14} />
        </td>
        <td style={cellStyle}>
          <div style={{ fontWeight: row.isCustom ? 600 : 400 }}>{row.subtradeLabel}</div>
          <div style={{ fontFamily: 'monospace', fontSize: '0.75em', ...muted }}>{row.code}</div>
          {row.note && (
            <div style={{ fontSize: '0.78em', ...muted, marginTop: 2 }}>{row.note}</div>
          )}
        </td>
        <td style={cellStyle}>{row.defaultUnit || <span style={muted}>—</span>}</td>
        <td style={cellStyle}><span style={muted}>{row.defaultLineType || '—'}</span></td>
        <td style={{ ...cellStyle, textAlign: 'right' }}>{row.templateSortOrder}</td>
        <td style={{ ...cellStyle, textAlign: 'right' }}>
          {row.usageCount} line{row.usageCount === 1 ? '' : 's'}
          <div style={{ fontSize: '0.75em', ...muted }}>
            {row.distinctProjects} project{row.distinctProjects === 1 ? '' : 's'}
          </div>
        </td>
        <td style={cellStyle}>
          {row.isCustom
            ? <span style={{ background: '#fff5da', color: '#856b09', padding: '2px 7px', borderRadius: 4, fontSize: '0.78em' }}>custom</span>
            : <span style={muted}>canonical</span>
          }
          {row.proposedAt && (
            <div style={{ fontSize: '0.72em', ...muted, marginTop: 2 }}>
              proposed {formatDate(row.proposedAt)}
            </div>
          )}
        </td>
        <td style={cellStyle}>
          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
            <BrioButton variant="secondary" disabled={submitting} onClick={() => setEditing(true)}>Edit</BrioButton>
            {row.isCustom ? (
              <>
                <BrioButton variant="primary" disabled={submitting} onClick={() => onPromote(row)}>Promote</BrioButton>
                <BrioButton variant="secondary" disabled={submitting || mergeDisabled} onClick={() => onMerge(row)}
                            title={mergeDisabled ? 'No canonical subtrades available under this trade' : ''}>
                  Merge…
                </BrioButton>
                <BrioButton variant="secondary" disabled={submitting || row.usageCount > 0} onClick={() => onReject(row)}
                            title={row.usageCount > 0 ? 'Still referenced by project lines. Merge first.' : ''}>
                  Reject
                </BrioButton>
              </>
            ) : (
              <BrioButton variant="secondary" disabled={submitting || row.usageCount > 0} onClick={() => onDelete(row)}
                          title={row.usageCount > 0 ? 'Still referenced by project lines. Rename or merge first.' : ''}>
                Delete
              </BrioButton>
            )}
          </div>
        </td>
      </tr>
    );
  }

  // Edit mode
  const save = async () => {
    const changes = {};
    if (draft.label.trim() && draft.label.trim() !== row.subtradeLabel) changes.label = draft.label.trim();
    if (draft.defaultUnit && draft.defaultUnit !== row.defaultUnit) changes.defaultUnit = draft.defaultUnit;
    if (draft.defaultLineType && draft.defaultLineType !== row.defaultLineType) changes.defaultLineType = draft.defaultLineType;
    const so = parseInt(draft.sortOrder, 10);
    if (Number.isFinite(so) && so !== row.templateSortOrder) changes.sortOrder = so;
    if ((draft.note || '').trim() !== (row.note || '').trim()) changes.note = draft.note;
    if (Object.keys(changes).length === 0) { setEditing(false); return; }
    const result = await onSave(row, changes);
    if (result?.success) setEditing(false);
  };

  return (
    <tr ref={setNodeRef} style={{ borderTop: '1px solid var(--brio-border-subtle, #eee)', background: '#fafaf6', ...dragStyle }}>
      <td style={{ ...cellStyle, width: 28, padding: '0.5rem 0.25rem', color: 'var(--brio-border-subtle, #ccc)' }}
          title="Save or cancel edit before reordering">
        <GripVertical size={14} />
      </td>
      <td style={cellStyle}>
        <BrioInput value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                   placeholder="Subtrade label" style={{ width: '100%', marginBottom: 4 }} />
        <BrioInput value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                   placeholder="Note (internal)" style={{ width: '100%', fontSize: '0.85em' }} />
      </td>
      <td style={cellStyle}>
        <BrioSelect value={draft.defaultUnit} onChange={(e) => setDraft({ ...draft, defaultUnit: e.target.value })}>
          {UNIT_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
        </BrioSelect>
      </td>
      <td style={cellStyle}>
        <BrioSelect value={draft.defaultLineType} onChange={(e) => setDraft({ ...draft, defaultLineType: e.target.value })}>
          {LINE_TYPE_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
        </BrioSelect>
      </td>
      <td style={{ ...cellStyle, textAlign: 'right' }}>
        <BrioInput type="number" value={draft.sortOrder}
                   onChange={(e) => setDraft({ ...draft, sortOrder: e.target.value })}
                   style={{ width: 80, textAlign: 'right' }} />
      </td>
      <td style={{ ...cellStyle, textAlign: 'right', ...muted }}>{row.usageCount} line{row.usageCount === 1 ? '' : 's'}</td>
      <td style={cellStyle}>{row.isCustom ? <span style={{ background: '#fff5da', color: '#856b09', padding: '2px 7px', borderRadius: 4, fontSize: '0.78em' }}>custom</span> : <span style={muted}>canonical</span>}</td>
      <td style={cellStyle}>
        <div style={{ display: 'flex', gap: '0.35rem' }}>
          <BrioButton variant="primary" disabled={submitting} onClick={save}>Save</BrioButton>
          <BrioButton variant="secondary" disabled={submitting} onClick={() => setEditing(false)}>Cancel</BrioButton>
        </div>
      </td>
    </tr>
  );
}

export default function CustomSubtradeReviewPage() {
  const {
    rows, loading, submitting, error,
    addSubtrade, updateSubtrade, deleteSubtrade,
    promoteSubtrade, mergeSubtrade, rejectSubtrade,
    reorderSubtrades, setRows,
  } = useBoqCustomSubtradeReview();
  const { taxonomies } = useBoqTaxonomies();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const [feedback, setFeedback] = useState(null);
  const [mergingRow, setMergingRow] = useState(null);
  const [addingToTrade, setAddingToTrade] = useState(null);

  // Group rows by trade chapter, preserving canonical trade order.
  const chapters = useMemo(() => {
    const byTrade = new Map();
    rows.forEach((r) => {
      if (!byTrade.has(r.tradeLabel)) {
        byTrade.set(r.tradeLabel, { label: r.tradeLabel, sort: r.tradeSortOrder, rows: [] });
      }
      byTrade.get(r.tradeLabel).rows.push(r);
    });
    return Array.from(byTrade.values()).sort((a, b) => a.sort - b.sort);
  }, [rows]);

  // Brio_trade order for fallback when no rows exist yet.
  const brioTradeOrder = useMemo(() => {
    const brio = taxonomies.find((t) => t.code === 'brio_trade');
    if (!brio) return [];
    return brio.nodes.filter((n) => !n.parentId).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  }, [taxonomies]);

  const reportResult = (result, successMsg) => {
    if (result?.success) {
      setFeedback({ tone: 'success', text: successMsg });
      setMergingRow(null);
      setAddingToTrade(null);
    } else if (result) {
      setFeedback({ tone: 'error', text: result.error });
    }
    return result;
  };

  const handleAdd = async (tradeLabel, fields) => {
    setFeedback(null);
    const result = await addSubtrade(tradeLabel, fields);
    reportResult(result, `Added "${fields.label}" to ${tradeLabel}.`);
  };

  const handleSave = async (row, changes) => {
    setFeedback(null);
    const result = await updateSubtrade(row.nodeId, changes);
    return reportResult(
      result,
      result?.result?.label_changed
        ? `Renamed "${result.result.old_label}" → "${result.result.new_label}" (${result.result.project_items_relabeled} line(s) relabeled).`
        : `Updated "${row.subtradeLabel}".`
    );
  };

  const handleDelete = async (row) => {
    setFeedback(null);
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete subtrade "${row.subtradeLabel}" from ${row.tradeLabel}?`)) return;
    const result = await deleteSubtrade(row.nodeId);
    reportResult(result, `Deleted "${row.subtradeLabel}".`);
  };

  const handlePromote = async (row) => {
    setFeedback(null);
    const result = await promoteSubtrade(row.nodeId);
    reportResult(result, `Promoted "${row.subtradeLabel}" into canonical.`);
  };

  const handleMergeConfirm = async (targetNodeId) => {
    setFeedback(null);
    const result = await mergeSubtrade(mergingRow.nodeId, targetNodeId);
    reportResult(
      result,
      `Merged "${result?.result?.merged_from}" → "${result?.result?.merged_into}" (${result?.result?.items_relabeled} lines).`
    );
  };

  const handleDragEnd = (chapterLabel) => async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const chapterIds = rows.filter((r) => r.tradeLabel === chapterLabel).map((r) => r.nodeId);
    const oldIndex = chapterIds.indexOf(active.id);
    const newIndex = chapterIds.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedIds = arrayMove(chapterIds, oldIndex, newIndex);

    // Optimistic local reorder: update each affected row's templateSortOrder
    // to the new 10-step value so the table re-renders in the new order even
    // before the RPC round-trips.
    const newOrderMap = new Map(reorderedIds.map((id, i) => [id, (i + 1) * 10]));
    setRows((prev) => prev.map((r) =>
      r.tradeLabel === chapterLabel && newOrderMap.has(r.nodeId)
        ? { ...r, templateSortOrder: newOrderMap.get(r.nodeId) }
        : r
    ));

    setFeedback(null);
    const result = await reorderSubtrades(reorderedIds);
    if (!result.success) {
      // RPC also refreshes on failure, so the optimistic update gets corrected.
      setFeedback({ tone: 'error', text: result.error });
    }
  };

  const handleReject = async (row) => {
    setFeedback(null);
    // eslint-disable-next-line no-alert
    const reason = window.prompt(`Reason for rejecting "${row.subtradeLabel}"? (optional)`);
    if (reason === null) return;
    const result = await rejectSubtrade(row.nodeId, reason);
    reportResult(result, `Rejected "${row.subtradeLabel}".`);
  };

  // Canonical targets for the Merge picker (same trade, is_custom=false).
  const canonicalTargetsFor = (row) =>
    rows.filter((r) => r.tradeLabel === row.tradeLabel && !r.isCustom && r.nodeId !== row.nodeId);

  return (
    <div>
      <BrioCard>
        <div style={{ marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Subtrade template</h2>
          <p style={{ margin: '0.4rem 0 0', fontSize: '0.85rem', color: 'var(--brio-text-muted)' }}>
            Every subtrade in the canonical brio_trade taxonomy, grouped by trade chapter.
            <strong> Edit</strong> renames a subtrade and propagates the new label to every project line that referenced it.
            Drag the <strong>⋮⋮</strong> handle to reorder within a chapter — the new order is persisted in 10-step increments so future inserts have room to slot between siblings.
            <strong> Custom</strong> rows (proposed by QS users from the BoQ tab) can be promoted into the canon, merged into an existing canonical sibling, or rejected.
            All actions are admin / supervisor only — the server returns a friendly error if a non-admin tries.
          </p>
        </div>

        <Banner tone={feedback?.tone}>{feedback?.text}</Banner>
        <Banner tone="error">{error}</Banner>

        {loading && <div style={{ padding: '1rem', color: 'var(--brio-text-muted)' }}>Loading…</div>}

        {!loading && chapters.length === 0 && (
          <div style={{ padding: '1rem', color: 'var(--brio-text-muted)' }}>
            No subtrades found. Run the brio_trade seed migration first.
          </div>
        )}

        {!loading && chapters.map((chapter, idx) => {
          const trade = brioTradeOrder.find((t) => t.label === chapter.label);
          const num = brioTradeOrder.indexOf(trade) + 1 || idx + 1;
          return (
            <div key={chapter.label} style={{ marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '1rem 0 0.5rem' }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: 0, color: 'var(--brio-text, #333)' }}>
                  {num}. {chapter.label}
                  <span style={{ marginLeft: 8, fontWeight: 400, color: 'var(--brio-text-muted)', fontSize: '0.85em' }}>
                    {chapter.rows.length} subtrade{chapter.rows.length === 1 ? '' : 's'}
                  </span>
                </h3>
                <BrioButton
                  variant="secondary"
                  disabled={submitting}
                  onClick={() => setAddingToTrade(chapter.label)}
                >
                  + Add subtrade
                </BrioButton>
              </div>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd(chapter.label)}
              >
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--brio-bg-subtle, #f7f7f1)', textAlign: 'left' }}>
                      <th style={{ padding: '0.5rem 0.25rem', width: 28 }}></th>
                      <th style={{ padding: '0.5rem 0.6rem', fontWeight: 600, width: '30%' }}>Subtrade</th>
                      <th style={{ padding: '0.5rem 0.6rem', fontWeight: 600, width: '10%' }}>Unit</th>
                      <th style={{ padding: '0.5rem 0.6rem', fontWeight: 600, width: '14%' }}>Line type</th>
                      <th style={{ padding: '0.5rem 0.6rem', fontWeight: 600, width: '8%', textAlign: 'right' }}>Sort</th>
                      <th style={{ padding: '0.5rem 0.6rem', fontWeight: 600, width: '10%', textAlign: 'right' }}>Usage</th>
                      <th style={{ padding: '0.5rem 0.6rem', fontWeight: 600, width: '10%' }}>Origin</th>
                      <th style={{ padding: '0.5rem 0.6rem', fontWeight: 600, width: '16%' }}>Actions</th>
                    </tr>
                  </thead>
                  <SortableContext
                    items={chapter.rows.map((r) => r.nodeId)}
                    strategy={verticalListSortingStrategy}
                  >
                    <tbody>
                      {chapter.rows.map((row) => (
                        <EditableRow
                          key={row.nodeId}
                          row={row}
                          submitting={submitting}
                          mergeDisabled={canonicalTargetsFor(row).length === 0}
                          onSave={handleSave}
                          onDelete={handleDelete}
                          onPromote={handlePromote}
                          onMerge={(r) => setMergingRow(r)}
                          onReject={handleReject}
                        />
                      ))}
                    </tbody>
                  </SortableContext>
                </table>
              </DndContext>
            </div>
          );
        })}
      </BrioCard>

      {mergingRow && (
        <MergePicker
          row={mergingRow}
          canonicalTargets={canonicalTargetsFor(mergingRow)}
          onCancel={() => setMergingRow(null)}
          onConfirm={handleMergeConfirm}
          submitting={submitting}
        />
      )}

      {addingToTrade && (
        <AddSubtradeModal
          tradeLabel={addingToTrade}
          onCancel={() => setAddingToTrade(null)}
          onConfirm={(fields) => handleAdd(addingToTrade, fields)}
          submitting={submitting}
        />
      )}
    </div>
  );
}
